/* eslint-disable no-restricted-imports */
import React, {useMemo, useState} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton'
import {T} from '@colonist/ui'
import styles from './UIGameActionButtonCityImprovements.module.scss'
import {UIGameCityImprovementTracker} from '../../../common/UIGameCityImprovementTracker/UIGameCityImprovementTracker'
import {CityImprovementTypes, TurnState} from '@colonist/game-states'
import {UIGameActionButtonCityImprovementsItem} from './UIGameActionButtonCityImprovementsItem'
import {useGameStore} from '../../../store/GameStoreContext'
import commonStyles from '../UIGameActionButtonContainer.module.scss'
import clsx from 'clsx'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator'

const CITY_IMPROVEMENTS_TYPES = [CityImprovementTypes.Science, CityImprovementTypes.Trade, CityImprovementTypes.Politics]
export interface UIGameActionButtonCityImprovementsProps {
    className?: string
    hideTooltip?: boolean
}

export const UIGameActionButtonCityImprovements = ({
    className,
    hideTooltip,
}: UIGameActionButtonCityImprovementsProps) => {
    const myColor = useGameStore((state) => state.myColor)
    const isPaused = useGameStore((state) => state.gamePause.isPaused)
    const currentState = useGameStore((state) => state.gameState.currentState)
    const currentStateValidator = useCurrentStateValidator()
    const isInitialPlacement = currentState.turnState === TurnState.InitialPlacement
    const isNotMyTurn = !currentStateValidator.currentTurnPlayers.includes(myColor)
    const disabled = isPaused || isInitialPlacement || isNotMyTurn
    const cityImprovementState = useGameStore((state) => state.gameState.mechanicCityImprovementsState)
    const playerCityImprovementState = cityImprovementState?.[myColor]
    const isOpen = useGameStore((state) => state.gameActionButtons.isCityImprovementExtraButtonsOpen)
    const setCityImprovementExtraButtonsOpen = useGameStore((state) => state.gameActionButtons.setCityImprovementExtraButtonsOpen)

    const [childButtonStates, setChildButtonStates] = useState<Record<CityImprovementTypes, boolean>>({
        [CityImprovementTypes.Trade]: false,
        [CityImprovementTypes.Politics]: false,
        [CityImprovementTypes.Science]: false,
    })

    if(!cityImprovementState || !playerCityImprovementState) {
        return null
    }

    const handleButtonClick = () => {
        setCityImprovementExtraButtonsOpen(!isOpen)
    }

    const hasAnyChildActionActive = useMemo(() => Object.values(childButtonStates).some((isDisabled) => !isDisabled), [childButtonStates])
    const isDisabledWithChildActions = disabled || !hasAnyChildActionActive

    const handleChildDisabledChange = (cityImprovementType: CityImprovementTypes) => (isDisabled: boolean) => {
        setChildButtonStates((prev) => ({...prev, [cityImprovementType]: isDisabled}))
    }

    return <div className={styles.container}>
        <UIGameActionButton
            className={className}
            disabled={isDisabledWithChildActions}
            onClick={handleButtonClick}
            onClickActionIfDisabled={handleButtonClick}
            customIconComponent={<UIGameCityImprovementTracker
                className={styles.cityImprovementTracker}
                cityImprovementState={playerCityImprovementState} />}
            iconClassName={styles.cityImprovementTrackerIcon}
            tooltipPosition='bottom'
            centerTextTooltip
            tooltipTitle={hideTooltip ? undefined : <T id='strings:game.tooltips.cityImprovements'/>}
        />
        <div className={clsx(styles.extraButtons, {
            [styles.hidden]: !isOpen,
        })}>
            {CITY_IMPROVEMENTS_TYPES
                .map((cityImprovementType) => <UIGameActionButtonCityImprovementsItem
                    key={cityImprovementType}
                    className={commonStyles.actionButton}
                    cityImprovementType={cityImprovementType}
                    hideTooltip={hideTooltip}
                    onDisabledChange={handleChildDisabledChange(cityImprovementType)}
                />)}
        </div>
    </div>
}
